import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  BOTTOM_NAV_BAR_HIDE_TIMING_CONFIG,
  BOTTOM_NAV_BAR_REVEAL_TIMING_CONFIG,
  DEFAULT_SPRING_CONFIG_INTRO,
} from '@experiences/blocs/utils/animations';
import { BottomNavItem } from '@/experiences/sales-portal/containers/sales-portal-tab-bar/components/bottom-nav-item';
import { BottomNavProfileItem } from '@/experiences/sales-portal/containers/sales-portal-tab-bar/components/bottom-nav-profile-item';
import { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import { createStyles } from '@/contexts/theme-context';
import { isWeb } from '@/utils/navigation';
import { LayoutChangeEvent, Platform } from 'react-native';
import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import { MobileBottomNavSkeleton } from '@/experiences/sales-portal/containers/sales-portal-tab-bar/components/mobile-bottom-nav-skeleton';
import { MobileNavigationDrawer } from '@/experiences/sales-portal/containers/mobile-navigation-drawer/mobile-navigation-drawer';
import {
  NavigationContext,
  NavigationRouteContext,
  NavigationState,
  Route,
  useRoute,
} from '@react-navigation/native';
import { Portal } from '@gorhom/portal';
import { SidenavSingleItem } from '@/domain/navigation/types';
import { useExperienceUIStore } from '@experiences/blocs/stores/experience-ui-store';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSalesPortalSidebarNav } from '@/experiences/sales-portal/blocs/hooks/use-sales-portal-sidebar-nav';
import { useSalesPortalSignedInEmployeeLoader } from '@/experiences/sales-portal/blocs/loaders/use-sales-portal-signed-in-employee-loader';
import { useShallow } from 'zustand/shallow';
import { XStack } from '@usepepper/sprout-kit-ui';

const VISIBLE_SLOT_COUNT = 3;
const INITIAL_TRANSLATE_Y = 100;

/**
 * Descends nested navigator state to find the focused leaf node
 * (aka the screen that's currently displayed to the user).
 *
 * @param state - Navigation state to descend from.
 * @returns The deepest focused route.
 */
const findActiveRoute = (state: NavigationState): Route<string> => {
  const route = state.routes[state.index];
  return route.state?.stale === false ? findActiveRoute(route.state) : route;
};

export const SalesPortalTabBar: React.FC<BottomTabBarProps> = ({
  descriptors,
  state,
}) => {
  const focusedTab = state.routes[state.index];

  const { layoutSize } = useLayoutContext();
  const isMobileBottomNavLayout = !isWeb && layoutSize === LayoutSize.SMALL;

  if (!isMobileBottomNavLayout) {
    return null;
  }

  return (
    <NavigationContext.Provider value={descriptors[focusedTab.key].navigation}>
      <NavigationRouteContext.Provider value={findActiveRoute(state)}>
        <SalesPortalTabBarContent />
      </NavigationRouteContext.Provider>
    </NavigationContext.Provider>
  );
};

// iOS's home indicator isn't visible unless tapped, so position the
// navbar slightly lower on the screen to look visually correct
const IOS_BOTTOM_INSET_TRIM_PX = 12;

const SalesPortalTabBarContent: React.FC = () => {
  const [styles] = useStyles();
  const { loading, navItems } = useSalesPortalSidebarNav();
  const { name: employeeName, uuid: employeeUUID } =
    useSalesPortalSignedInEmployeeLoader();
  const insets = useSafeAreaInsets();
  const barPaddingBottom =
    Platform.OS === 'ios'
      ? Math.max(insets.bottom - IOS_BOTTOM_INSET_TRIM_PX, 0)
      : insets.bottom;

  const [barHeight, setBarHeight] = useState(0);
  const isHidden = useExperienceUIStore((s) => s.isBottomNavHidden);

  const translateY = useSharedValue(INITIAL_TRANSLATE_Y);
  useEffect(() => {
    translateY.value = withSpring(0, DEFAULT_SPRING_CONFIG_INTRO);
  }, [translateY]);

  // Only animate the bar appearing after it's been hidden; don't animate it on first-render
  const hasHiddenRef = useRef(false);
  useEffect(() => {
    if (!hasHiddenRef.current && !isHidden) {
      return;
    }
    hasHiddenRef.current = true;
    translateY.value = withTiming(
      isHidden ? barHeight : 0,
      isHidden
        ? BOTTOM_NAV_BAR_HIDE_TIMING_CONFIG
        : BOTTOM_NAV_BAR_REVEAL_TIMING_CONFIG,
    );
  }, [isHidden, barHeight, translateY]);

  // Show the bottom nav on route change
  // (and reset the store if the Screen is unmounted, e.g. transition to Storefront)
  const { key: routeKey } = useRoute();
  useEffect(
    () => () => useExperienceUIStore.getState().setBottomNavHidden(false),
    [routeKey],
  );

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  const handleLayout = (event: LayoutChangeEvent) => {
    setBarHeight(event.nativeEvent.layout.height);
    useExperienceUIStore
      .getState()
      .setBottomNavHeight(event.nativeEvent.layout.height);
  };

  // defensively keep single (tappable) items only
  const singleItems = navItems.filter(
    (item): item is SidenavSingleItem => !item.subnavItems,
  );
  const visibleItems = singleItems.slice(0, VISIBLE_SLOT_COUNT);
  const overflowItems = singleItems.slice(VISIBLE_SLOT_COUNT);

  const isOverflowDrawerOpen = useExperienceUIStore(
    useShallow((state) => state.isMobileDrawerOpen),
  );
  const isProfileActive =
    isOverflowDrawerOpen || overflowItems.some((item) => item.active);

  const toggleProfileDrawer = useCallback(() => {
    useExperienceUIStore.getState().toggleMobileDrawer();
  }, []);

  const onOverflowDrawerChange = useCallback((open: boolean) => {
    useExperienceUIStore.getState().setMobileDrawerOpen(open);
  }, []);

  return (
    <>
      <Animated.View
        onLayout={handleLayout}
        style={[{ paddingBottom: barPaddingBottom }, styles.bar, animatedStyle]}
      >
        {loading ? (
          <MobileBottomNavSkeleton />
        ) : (
          <XStack flex={1} justifyContent="space-between">
            {visibleItems.map((item) => (
              <BottomNavItem
                active={item.active}
                icon={item.icon}
                iconUrl={item.iconUrl}
                key={item.id ?? item.label}
                label={item.label}
                onPress={item.onPress}
              />
            ))}
            <BottomNavProfileItem
              active={isProfileActive}
              label={i18n.t('sales_portal.sidebar.profile')}
              name={employeeName}
              onPress={toggleProfileDrawer}
              uuid={employeeUUID}
            />
          </XStack>
        )}
      </Animated.View>
      {/* Drawer required position absolute, and thus must be in a Portal above the main content. */}
      <Portal>
        <MobileNavigationDrawer
          employeeName={employeeName}
          employeeUUID={employeeUUID}
          onOpenChange={onOverflowDrawerChange}
          open={isOverflowDrawerOpen}
          overflowItems={overflowItems}
        />
      </Portal>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  bar: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderTopColor: theme.sk.color.border.primary,
    borderTopWidth: 1,
    bottom: 0,
    flexDirection: 'row',
    left: 0,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    position: 'absolute',
    right: 0,
  },
}));
